'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { findCityBySlugOrName } from '../_data/catalogue';
import { ListingSkeleton } from './Skeletons';

/**
 * BCP-2053 — autofill the city from the visitor's last search in another module.
 *
 * The shared store is the `theatre-searches-city` localStorage blob that
 * hotels, sports, concerts and restaurants already write on a city search. This
 * reads it exactly the way concerts (`ConcertsHomeListing.readCityFromStorage`)
 * and sports (`SearchBar`, priority 1) do — the same key, the same `.city`
 * field, the same treat-corrupt-as-absent rule. Nothing new is stored and
 * nothing here writes, so no other module's behaviour can change.
 *
 * It runs on the CLIENT because the blob is localStorage, which the server
 * render cannot see. The page resolves its destination server-side, so rather
 * than introduce a second resolution path this navigates to the ordinary
 * `?city=` URL and lets `page.tsx` branch 1 do the resolving — one resolver,
 * and the resulting URL is the same shareable link a manual search produces.
 *
 * Three guards keep it from changing behaviour it should not:
 *
 *  1. `active` is true ONLY when the visitor named no city at all. That is the
 *     same slot the geo default already occupies (`page.tsx` branch 3, "only
 *     when the visitor named none"), so this takes priority over geo without
 *     ever overriding an explicit `?city=`/`?cityId=` — and an unknown named
 *     city still lands on its honest empty state rather than being silently
 *     substituted.
 *  2. The stored city must RESOLVE in our own catalogue before we navigate. A
 *     city sold by sports but not by attractions would otherwise replace a
 *     working geo default with a zero-result page — strictly worse than doing
 *     nothing. Unresolvable → leave the page exactly as the server rendered it.
 *  3. It never navigates to the city already being shown, so the geo default
 *     agreeing with the stored city costs no second render.
 *
 * After it navigates the URL carries `?city=`, so `active` is false on the next
 * render and it cannot loop.
 */
const SHARED_CITY_KEY = 'theatre-searches-city';

/** The stored city name, or '' when absent, corrupt, or storage is unavailable. */
export function readSharedCity(): string {
  if (typeof window === 'undefined') return '';
  try {
    const raw = localStorage.getItem(SHARED_CITY_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw) as { city?: string } | null;
    return (parsed?.city ?? '').trim();
  } catch {
    // Corrupt JSON, or storage disabled/blocked — treat as no stored city.
    return '';
  }
}

const sameCity = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

interface CityAutofillSyncProps {
  /** True only when the URL named no city — see guard 1. */
  active: boolean;
  /** The city the server already resolved (geo default), so we can skip a no-op. */
  currentCity: string;
  /**
   * The listing. While `active`, it is held behind the listing skeleton until
   * the stored city is resolved — the same "resolve on mount, skeleton while
   * resolving" approach as `ConcertsHomeListing`. Painting the server's geo
   * default first is what showed London before switching to the stored city.
   */
  children?: ReactNode;
}

export function CityAutofillSync({ active, currentCity, children }: CityAutofillSyncProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  // True on the server AND the first client render, so hydration matches.
  const [pending, setPending] = useState(true);
  // React 18 StrictMode runs effects twice in dev; the resolve is a network
  // call, so hold a ref rather than firing it (and the navigation) twice.
  const ran = useRef(false);
  // Unmount only. Deliberately NOT the main effect's cleanup: that cleanup runs
  // on any dependency change too (`useRouter()`'s identity is not guaranteed
  // stable across renders), which would cancel the in-flight resolve while
  // `ran` already blocks the retry — the autofill would silently never fire.
  const alive = useRef(true);
  useEffect(() => () => {
    alive.current = false;
  }, []);

  useEffect(() => {
    if (!active || ran.current) return;

    const stored = readSharedCity();
    if (!stored || sameCity(stored, currentCity)) {
      setPending(false);
      return;
    }

    ran.current = true;

    (async () => {
      try {
        const match = await findCityBySlugOrName(stored);
        // Guard 2 — we do not sell there; keep the server's default.
        // `alive` so a visitor who has already navigated away is not yanked
        // back to the listing by a late resolve.
        if (!alive.current) return;
        if (!match) {
          setPending(false);
          return;
        }

        const params = new URLSearchParams(searchParams.toString());
        // Same value the hero's own submit writes, so an autofilled listing and
        // a manually searched one produce byte-identical URLs.
        params.set('city', match.urlSlug || match.citySlug || match.city);
        // `replace`, not `push`: the visitor never chose the geo-default page,
        // so Back must leave attractions rather than bounce them through it.
        // The skeleton stays up until the `?city=` render arrives (`active`
        // turns false), so the default city is never painted in between.
        router.replace(`/attractions-experiences?${params.toString()}`);
      } catch {
        // Resolution failed (offline, service down) — the server-rendered
        // listing is still perfectly usable, so reveal it.
        if (alive.current) setPending(false);
      }
    })();
  }, [active, currentCity, router, searchParams]);

  if (active && pending) return <ListingSkeleton />;
  return <>{children}</>;
}
